import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as CreditActions from "../../../store/credit/actions";
import { ApplicationState } from "../../../store";
import moment from "moment-timezone";
import CreditScoreType from "./CreditScoreType";
import CreditExpired from "../CreditExpired";
import CreditStatus from "../CreditStatus";
import { getScoreRating, getScoreStatus, useBureauPermission, fetchCreditData } from "../../../util/credit";
import { ezsServiceIds, idfServiceIds } from "../../../config/UserServicesList";
import { useAreServicesActive } from "../../../util/CommonServices";
import TransunionLogo from "../../../Assets/images/logo_transunion.png";
import ExperianLogo from "../../../Assets/images/logo_experian.png";
import EquifaxLogo from "../../../Assets/images/logo_equifax.png";
import { useTranslation } from "react-i18next";

const CreditScoreAllBureau = () => {
    const dispatch = useDispatch();
    const { t: tLang } = useTranslation();
    const bureauPermission = useBureauPermission();
    const isTUUser = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userInfo?.isTUUser);
    const hasOneTimeScore = useSelector((state: ApplicationState) => state.creditHistoryData && state.creditHistoryData.hasOneTimeScore);
    let viewedOneTimeScore = useSelector((state: ApplicationState) => {
        return state.oneTimeCreditData.pageViewScore;
    });
    const purchasedOneTimeScore = useSelector((state: ApplicationState) => {
        return state.oneTimeCreditData.purchasedReport;
    });
    const partnerPhoneNumber: string = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.phoneNumber
            ? state.configurationState.configuration.partnerConfig.phoneNumber
            : ""
    );
    // Start Active service base UI Display Logic
    const scoreOnlyIds = [
        idfServiceIds.creditScoreMonitoring,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.tuCreditScoreTracker,
        ezsServiceIds.tuCanadaCreditScoreTracker,
        ezsServiceIds.expMonthlyCreditScoreTracker,
    ];
    const hasCreditScoreOnlyService = useAreServicesActive(scoreOnlyIds);

    const prioritizedServiceCode = useSelector((state: ApplicationState) => {
        if (state.creditHistoryData.isOneTimeOnlyScore && !hasCreditScoreOnlyService) {
            return state.creditHistoryData.oneTimeScoreServiceCode;
        } else {
            return "";
        }
    });
    const requestCreditData = () => dispatch(CreditActions.actionCreators.requestCreditData(false, bureauPermission, prioritizedServiceCode));

    let creditScores = useSelector((state: ApplicationState) => {
        if (hasOneTimeScore && state?.oneTimeCreditData?.oneTimeCreditData?.scores.length > 0 && purchasedOneTimeScore && !viewedOneTimeScore) {
            if (state.oneTimeCreditData.oneTimeCreditData != null && state.oneTimeCreditData.oneTimeCreditData.scores != null) {
                state.oneTimeCreditData.oneTimeCreditData.scores.forEach(function (score) {
                    let scoreDesc = getScoreRating(score["risk-score"], tLang);
                    score["score-desc"] = scoreDesc;

                    if (!state.oneTimeCreditData.oneTimeCreditData.IsIDFUser && !isTUUser && score["inquiry-date"] == null) {
                        // for EZS user dont show updated date.
                        //score["inquiry-date"] = state.creditState.creditData["update-date"];
                    } else if (!score["inquiry-date"].includes("/")) {
                        score["inquiry-date"] = moment(score["inquiry-date"], "YYYY-MM-DD").format("MM/DD/YYYY");
                    }
                });
            }
            return state.oneTimeCreditData.oneTimeCreditData;
        } else {
            if (state.creditState.creditData && state.creditState.creditData.length > 0) {
                let scoreData = fetchCreditData(state.creditState.creditData, "score");
                if (scoreData && scoreData.scores != null) {
                    scoreData.scores.forEach(function (score: any) {
                        let scoreDesc = getScoreRating(score["risk-score"], tLang);
                        score["score-desc"] = scoreDesc;

                        if (!scoreData.IsIDFUser && !isTUUser && score["inquiry-date"] == null) {
                            // for EZS user dont show updated date.
                            //score["inquiry-date"] = state.creditState.creditData["update-date"];
                        } else if (!score["inquiry-date"].includes("/")) {
                            score["inquiry-date"] = moment(score["inquiry-date"], "YYYY-MM-DD").format("MM/DD/YYYY");
                        }
                    });
                }
                return scoreData;
            } else {
                return null;
            }
        }
    });

    const isLoading = useSelector((state: ApplicationState) => {
        return state.creditState.isLoading;
    });

    const isCreditHistoryLoading = useSelector((state: ApplicationState) => {
        return state.creditHistoryData.isLoading;
    });

    const ensureDataFetched = () => {
        requestCreditData();
    };

    useEffect(ensureDataFetched, [isCreditHistoryLoading]);

    const renderMessageScreen = () => {
        if (creditScores && (creditScores.IsIDFUser || isTUUser) && (creditScores["tu-status"] === "tu-thin-error" || creditScores["tu-status"] === "tu-down")) {
            return <CreditStatus status={creditScores["tu-status"]} />;
        } else if (creditScores && (creditScores.IsIDFUser || isTUUser) && creditScores.IsScoreExpired) {
            return <CreditExpired />;
        } else {
            return renderScoreScreen();
        }
    };

    const getCreditBureauLogo = (bureau: string) => {
        bureau = bureau.toLowerCase();
        let creditBureauLogo;
        switch (bureau) {
            case "transunion":
                creditBureauLogo = TransunionLogo;
                break;
            case "experian":
                creditBureauLogo = ExperianLogo;
                break;
            case "equifax":
                creditBureauLogo = EquifaxLogo;
                break;
        }

        return creditBureauLogo;
    };

    const renderScoreScreen = () => {       
        if (creditScores && creditScores.scores) {
            creditScores.scores.forEach((score: any, index: any) => {
                if (score.abbreviation && score.abbreviation === "TransUnion") {
                    creditScores && creditScores.scores.splice(index, 1);
                    creditScores && creditScores.scores.unshift(score);
                }
            });
        }

        return (
            <ul className="credit-bureau-list">
                {creditScores &&
                    creditScores.scores &&
                    creditScores.scores.map((score: any) => (
                        <CreditScoreType
                            key={score.type}
                            scoreValue={score["risk-score"]}
                            scoreRating={score["score-desc"]}
                            population={score["population-rank"]}
                            title={score.abbreviation}
                            logo={getCreditBureauLogo(score.abbreviation)}
                            updated={score["inquiry-date"]}
                            isIDFUser={creditScores && creditScores.IsIDFUser ? creditScores.IsIDFUser : false}
                            status={getScoreStatus(score["risk-score"])}
                            isTUUser={isTUUser}
                        />
                    ))}
            </ul>
        );
    };

    return (
        <React.Fragment>
            {creditScores != null ? (
                <React.Fragment>
                    <div>{renderMessageScreen()}</div>
                </React.Fragment>
            ) : (
                <React.Fragment>{isLoading ? <div className="box loading"></div> : ""}</React.Fragment>
            )}
        </React.Fragment>
    );
};

export default CreditScoreAllBureau;
